import axios from "../../config/axios";
import { useToast } from 'vue-toastification';
import { loginWithGoogle } from '@/utils/oauth';
import { clearTrendingCache } from '@/utils/trendingCache';

const toast = useToast();

export default {
  name: 'Register',
  data() {
    return {
      username: '',
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      yob: '',
      agreeTerms: false,
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      isGoogleLoading: false,
      isOtpStep: false,
      otpCode: '',
      isOtpLoading: false,
      isResendingOtp: false,
      accountId: null,
      otpCountdown: 0,
      countdownTimer: null,
      errors: {
        username: '',
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        yob: '',
        terms: '',
        otp: ''
      },
      quoteIndex: 0,
      quotes: [
        { text: "Một cuốn sách hay trên giá là một người bạn dù quay lưng lại vẫn là người bạn tốt.", author: "Khuyết danh" },
        { text: "Sách là nguồn tài nguyên vô tận, đọc sách giúp chúng ta khám phá những chân trời mới.", author: "Victor Hugo" },
        { text: "Việc đọc tất cả những cuốn sách hay cũng như trò chuyện với những bộ óc tuyệt vời nhất của các thế kỷ đã qua.", author: "René Descartes" }
      ],
      quoteTimer: null
    };
  },
  computed: {
    currentQuote() {
      return this.quotes[this.quoteIndex];
    },
    formattedCountdown() {
      const minutes = Math.floor(this.otpCountdown / 60);
      const seconds = this.otpCountdown % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  },
  mounted() {
    this.startQuoteRotation();
  },
  beforeUnmount() {
    if (this.quoteTimer) {
      clearInterval(this.quoteTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    clearError(field) {
      this.errors[field] = '';
    },
    validateForm() {
      let isValid = true;

      // Username check
      if (!this.username) {
        this.errors.username = "Vui lòng nhập tên đăng nhập";
        isValid = false;
      } else if (this.username.length < 5 || this.username.length > 20) {
        this.errors.username = "Tên đăng nhập phải chứa từ 5 đến 20 ký tự";
        isValid = false;
      }

      // Full Name check
      if (!this.fullName) {
        this.errors.fullName = "Vui lòng nhập họ và tên";
        isValid = false;
      } else if (this.fullName.length < 5 || this.fullName.length > 100) {
        this.errors.fullName = "Họ và tên phải chứa từ 5 đến 100 ký tự";
        isValid = false;
      }

      // Year of Birth check
      const currentYear = new Date().getFullYear();
      const age = currentYear - parseInt(this.yob);
      if (!this.yob) {
        this.errors.yob = "Vui lòng nhập năm sinh";
        isValid = false;
      } else if (isNaN(this.yob) || age < 13 || age > 100) {
        this.errors.yob = "Tuổi phải từ 13 đến 100 tuổi";
        isValid = false;
      }

      // Email check
      if (!this.email) {
        this.errors.email = "Vui lòng nhập địa chỉ email";
        isValid = false;
      } else if (this.email.length < 5 || this.email.length > 50) {
        this.errors.email = "Email phải chứa từ 5 đến 50 ký tự";
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.errors.email = "Địa chỉ email không hợp lệ";
          isValid = false;
        }
      }

      // Password check
      if (!this.password) {
        this.errors.password = "Vui lòng nhập mật khẩu";
        isValid = false;
      } else if (this.password.length < 6 || this.password.length > 20) {
        this.errors.password = "Mật khẩu phải chứa từ 6 đến 20 ký tự";
        isValid = false;
      }

      // Confirm Password check
      if (!this.confirmPassword) {
        this.errors.confirmPassword = "Vui lòng nhập lại mật khẩu";
        isValid = false;
      } else if (this.confirmPassword !== this.password) {
        this.errors.confirmPassword = "Mật khẩu xác nhận không khớp";
        isValid = false;
      }

      // Terms check
      if (!this.agreeTerms) {
        this.errors.terms = "Bạn phải đồng ý với điều khoản dịch vụ";
        isValid = false;
      }

      return isValid;
    },
    handleRegister() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      const requestData = {
        username: this.username,
        fullname: this.fullName,
        password: this.password,
        email: this.email,
        yob: parseInt(this.yob)
      };

      axios.post("account/v1/register-user", requestData)
        .then(response => {
          if (response.data.status === '500') {
            this.isLoading = false;
            toast.error(response.data.message);
          } else if (response.data.status === '200') {
            if (response.data.message === 'OTP_REQUIRED') {
              this.accountId = response.data.result;
              this.isOtpStep = true;
              this.isLoading = false;
              this.startOtpCountdown();
              toast.info('Mã OTP đã được gửi đến email của bạn!');
              return;
            }
            
            toast.success(response.data.message);

            const result = response.data.result;
            const fullName = result.fullName || result.fullname || "";
            const username = result.username || this.username;

            clearTrendingCache();
            localStorage.setItem(
              "current-account",
              JSON.stringify({
                id: result.id,
                username: username,
                authority: result.authority,
                name: fullName,
                fullName: fullName
              })
            );

            const token = result.token;
            localStorage.setItem("token", token);
            if (result.refreshToken) {
              localStorage.setItem("refreshToken", result.refreshToken);
            }

            axios.defaults.headers['Authorization'] = `Bearer ${token}`;

            // Thông báo cho Home xóa cache đề xuất cũ và gọi API mới
            window.dispatchEvent(new CustomEvent('user-logged-in'));

            this.isLoading = false;
            this.$router.push('/user-edit?from=register');
          } else {
            this.isLoading = false;
            toast.error(response.data.message);
          }
        })
        .catch(error => {
          this.isLoading = false;
          toast.error(error.response?.data?.message || error.message);
        });
    },
    startOtpCountdown() {
      this.otpCountdown = 180;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.countdownTimer = setInterval(() => {
        if (this.otpCountdown > 0) {
          this.otpCountdown--;
        } else {
          clearInterval(this.countdownTimer);
        }
      }, 1000);
    },
    handleResendOtp() {
      if (this.isResendingOtp || !this.accountId) return;
      
      this.isResendingOtp = true;
      axios.post("account/v1/resend-otp", {
        accountId: this.accountId
      })
      .then(response => {
        this.isResendingOtp = false;
        if (response.data.status === '200') {
          toast.success("Mã OTP mới đã được gửi!");
          this.startOtpCountdown();
        } else {
          toast.error(response.data.message || "Lỗi gửi lại mã OTP");
        }
      })
      .catch(error => {
        this.isResendingOtp = false;
        toast.error(error.response?.data?.message || "Lỗi hệ thống khi gửi lại mã");
      });
    },
    handleVerifyOtp() {
      if (!this.otpCode || this.otpCode.length < 6) {
        this.errors.otp = "Vui lòng nhập đủ 6 số OTP";
        return;
      }
      
      this.isOtpLoading = true;
      
      axios.post("account/v1/verify-register-otp", {
        accountId: this.accountId,
        otp: this.otpCode
      })
      .then(response => {
        if (response.data.status === '200') {
          toast.success("Kích hoạt tài khoản thành công!");
          
          const result = response.data.result;
          const fullName = result.fullName || result.fullname || this.fullName;
          const username = result.username || this.username;

          clearTrendingCache();
          localStorage.setItem(
            "current-account",
            JSON.stringify({
              id: result.id,
              username: username,
              authority: result.authority,
              name: fullName,
              fullName: fullName
            })
          );

          const token = result.token;
          localStorage.setItem("token", token);
          if (result.refreshToken) {
            localStorage.setItem("refreshToken", result.refreshToken);
          }

          axios.defaults.headers['Authorization'] = `Bearer ${token}`;

          // Thông báo cho Home xóa cache đề xuất cũ và gọi API mới
          window.dispatchEvent(new CustomEvent('user-logged-in'));

          this.isOtpLoading = false;
          this.$router.push('/user-edit?from=register');
        } else {
          this.isOtpLoading = false;
          this.errors.otp = response.data.message || "Mã OTP không hợp lệ";
        }
      })
      .catch(error => {
        this.isOtpLoading = false;
        this.errors.otp = error.response?.data?.message || "Lỗi xác thực OTP";
      });
    },
    async handleGoogleLogin() {
      this.isGoogleLoading = true;
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;

      try {
        const idToken = await loginWithGoogle(clientId);
        const response = await axios.post("account/v1/google", {
          idToken: idToken
        });

        if (response.data && response.data.status === '200' && response.data.result) {
          const result = response.data.result;
          const fullName = result.fullName || result.fullname || "";
          const username = result.username || "";

          clearTrendingCache();
          localStorage.setItem(
            "current-account",
            JSON.stringify({
              id: result.id,
              username: username,
              authority: result.authority,
              name: fullName,
              fullName: fullName
            })
          );

          const token = result.token;
          localStorage.setItem("token", token);
          if (result.refreshToken) {
            localStorage.setItem("refreshToken", result.refreshToken);
          }

          axios.defaults.headers['Authorization'] = `Bearer ${token}`;

          // Thông báo cho Home xóa cache đề xuất cũ và gọi API mới
          window.dispatchEvent(new CustomEvent('user-logged-in'));

          toast.success("Đăng nhập bằng Google thành công!");
          this.$router.push('/');
        } else {
          toast.error(response.data?.message || "Đăng nhập Google thất bại, vui lòng thử lại.");
        }
      } catch (error) {
        console.error("Google login error:", error);
        toast.error(error.message || "Không thể đăng nhập bằng Google.");
      } finally {
        this.isGoogleLoading = false;
      }
    },
    startQuoteRotation() {
      this.quoteTimer = setInterval(() => {
        this.quoteIndex = (this.quoteIndex + 1) % this.quotes.length;
      }, 6000);
    }
  }
};
